import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import { LangProvider, useLang } from "./contexts/LangContext";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import NotificationsBell from "./components/NotificationsBell";
import LanguageSwitcher from "./components/LanguageSwitcher";
import ApilyLogo from "./components/ApilyLogo";
import { SiteFooter } from "./components/SiteFooter";
import { GuidesFab } from "./components/GuidesFab";
import { PublicBackLayout } from "./components/PublicBackButton";
import { AuthorizedUnknownRoute, GuestUnknownRoute } from "./screens/NotFoundScreen";

import { LogIn, LogOut, Menu, BookOpen, Plug2, User } from "lucide-react";
import { APP_ROUTES, isPrivatePath, legacyAppPath } from "./const/appRoutes";
import { UserProvider, useUser } from "./contexts/userContext";
import RouteLoadingFallback, { SidebarLoadingFallback } from "./components/RouteLoadingFallback";
import { hasSessionCandidate, shouldShowSessionGate } from "./routeSessionState";
import { trackMatomoPageView } from "./utils/matomo";
import {
  ensureTelegramWebAppScript,
  isTelegramRuntime,
} from "./utils/telegramWebApp";

const HomeScreen = lazy(() => import("./screens/HomeScreen"));
const ReferralsScreen = lazy(() => import("./screens/ReferralsScreen"));
const SupportScreen = lazy(() => import("./screens/SupportScreen"));
const FunctionsScreen = lazy(() => import("./screens/FunctionsScreen"));
const ApiScreen = lazy(() => import("./screens/ApiScreen"));
const ModelsScreen = lazy(() => import("./screens/ModelsScreen"));
const DocsScreen = lazy(() => import("./screens/DocsScreen"));
const BillingScreen = lazy(() => import("./screens/BillingScreen"));
const StudioScreen = lazy(() => import("./screens/StudioScreen"));
const ProfileScreen = lazy(() => import("./screens/ProfileScreen"));
const LandingScreen = lazy(() => import("./screens/LandingScreen"));
const LegalScreen = lazy(() => import("./screens/LegalScreen"));
const PublicGuidesScreen = lazy(() => import("./screens/PublicGuidesScreen"));
const PublicGuideScreen = lazy(() => import("./screens/PublicGuideScreen"));
const publicIntentScreens = () => import("./screens/PublicIntentScreens");
const PublicOpenAiScreen = lazy(() =>
  publicIntentScreens().then((m) => ({ default: m.PublicOpenAiScreen })),
);
const PublicAnthropicScreen = lazy(() =>
  publicIntentScreens().then((m) => ({ default: m.PublicAnthropicScreen })),
);
const PublicPricingScreen = lazy(() =>
  publicIntentScreens().then((m) => ({ default: m.PublicPricingScreen })),
);
const PublicCompareScreen = lazy(() =>
  publicIntentScreens().then((m) => ({ default: m.PublicCompareScreen })),
);
const Sidebar = lazy(() => import("./components/Sidebar"));
const AuthModal = lazy(() => import("./components/AuthModal"));
const CompleteAccountModal = lazy(() => import("./components/CompleteAccountModal"));
const Toaster = lazy(() =>
  import("./components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

function AppInner() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLang();
  const { user, loading, logout, login, pendingTwoFactor, clearTwoFactor } = useUser();
  const isAuthorized = Boolean(user);
  const emailVerifyHandled = useRef(false);
  const studioReturnPath = useRef(null);
  const screenWrapperRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const legacyPath = legacyAppPath(path);
  const privateRoute = isPrivatePath(path);
  const candidateSession = hasSessionCandidate({
    accessToken: window.localStorage.getItem("accessToken"),
    search: location.search,
    telegramRuntime: isTelegramRuntime(),
  });
  const showSessionGate = shouldShowSessionGate({
    pathname: path,
    loading,
    isAuthorized,
    hasCandidate: candidateSession,
  });
  const protectedShell = isAuthorized || (showSessionGate && Boolean(candidateSession || privateRoute || legacyPath));
  const isRouteHandoff = path === "/" || path === "/landing" || path.startsWith("/ref_");
  const loadingPath = isRouteHandoff
    ? (candidateSession ? APP_ROUTES.home : "/")
    : path;
  const routeRoot = path.split("/")[1];
  const activeScreen = routeRoot === "guides" ? "integrations"
    : routeRoot === "api-keys" ? "api" : routeRoot;
  // Docs is the one screen wider than the shared --content-max-width scale (it needs the room
  // for tables/code, per user feedback) — the header has to widen to match on that route only.
  const isDocsRoute = activeScreen === "docs" || location.pathname.startsWith("/docs");
  // Studio is a full-bleed workspace: the nav collapses to an icon rail so the chat gets the room.
  const isStudioRoute = protectedShell && activeScreen === "studio";
  // The guides index is the button's own target, so it stays hidden there only.
  const showGuidesFab = !showSessionGate && path !== APP_ROUTES.integrations;
  const floatingGuidesFab = showGuidesFab && !isStudioRoute;
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // <Link> keeps the previous scroll offset, so a new page would open at the footer it was left from.
  // The screen wrapper is the scroller in the app shell; the window scrolls where the wrapper does not.
  useEffect(() => {
    if (location.hash) return;
    const wrapper = screenWrapperRef.current;
    if (wrapper) {
      // The wrapper has `scroll-behavior: smooth`, which a route swap interrupts mid-animation.
      wrapper.style.scrollBehavior = "auto";
      wrapper.scrollTop = 0;
      wrapper.style.scrollBehavior = "";
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) {
      robots.content = privateRoute ? "noindex,follow"
        : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
    }
  }, [privateRoute]);

  useEffect(() => {
    if (pendingTwoFactor && !user) {
      setAuthTab("login");
      setAuthOpen(true);
    }
  }, [pendingTwoFactor, user]);

  useEffect(() => {
    if (emailVerifyHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const v = params.get("emailVerify");
    if (!v) return;
    emailVerifyHandled.current = true;
    const next = new URLSearchParams(params);
    next.delete("emailVerify");
    const path = window.location.pathname;
    const rest = next.toString();
    window.history.replaceState(
      {},
      "",
      `${path}${rest ? `?${rest}` : ""}${window.location.hash}`,
    );
    if (v === "ok") {
      toast.success(t("email_verify_toast_ok"));
      login();
    } else if (v === "invalid") {
      toast.error(t("email_verify_toast_invalid"));
    } else if (v === "missing") {
      toast.error(t("email_verify_toast_missing"));
    } else {
      toast.error(t("email_verify_toast_error"));
    }
  }, [login, t]);

  // Обработка реферальных ссылок: /ref_<userId> или ?ref=<userId>, и промокода: ?promo=<CODE>
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    // Проверяем формат /ref_<userId>
    if (path.startsWith('/ref_')) {
      const refCode = path.substring(1); // убираем первый слеш
      localStorage.setItem('referralCode', refCode);
      // Редиректим на главную
      navigate('/', { replace: true });
      return;
    }

    // Проверяем формат ?ref=<userId>
    const refParam = params.get('ref');
    if (refParam) {
      localStorage.setItem('referralCode', `ref_${refParam}`);
      params.delete('ref');
    }

    // Проверяем формат ?promo=<CODE>
    const promoParam = params.get('promo');
    if (promoParam) {
      localStorage.setItem('promoCode', promoParam);
      params.delete('promo');
    }

    if (refParam || promoParam) {
      // Убираем ref/promo из URL
      const newSearch = params.toString();
      navigate(
        {
          pathname: path,
          search: newSearch ? `?${newSearch}` : '',
          hash: window.location.hash,
        },
        { replace: true },
      );
    }
  }, [navigate]);

  useEffect(() => {
    if (loading || legacyPath) return;
    // The routing effect below redirects these at once; only the destination counts as a view.
    const redirected = isAuthorized
      ? path === "/" || path === "/landing"
      : path === "/landing" || privateRoute;
    if (redirected) return;
    trackMatomoPageView(path);
  }, [isAuthorized, loading, legacyPath, privateRoute, path]);

  useEffect(() => {
    // Server 301s cover direct visits; client navigation also normalizes old links.
    if (legacyPath) {
      navigate(`${legacyPath}${location.search}${location.hash || ""}`, { replace: true });
      return;
    }
    if (loading) return;
    const path = location.pathname;
    if (isAuthorized) {
      if (path === "/" || path === "/landing") {
        navigate(studioReturnPath.current || APP_ROUTES.home, { replace: true });
        studioReturnPath.current = null;
      }
    } else if (path === "/landing" || privateRoute) {
      if (path === "/studio" || path.startsWith("/studio/")) {
        studioReturnPath.current = `${path}${location.search}${location.hash || ""}`;
        setAuthTab("login");
        setAuthOpen(true);
      }
      navigate("/", { replace: true });
    }
  }, [isAuthorized, loading, legacyPath, privateRoute, location.pathname, location.search, location.hash, navigate]);

  // Загружает Telegram WebApp SDK и сохраняет реферальный/промокод из start_param в
  // localStorage. Саму авторизацию (POST /api/auth/telegram) выполняет UserProvider
  // (userContext.js resolveSession) — он единственный источник истины для сессии Mini App,
  // здесь только подготовка данных до того, как он отправит запрос.
  useEffect(() => {
    if (!isTelegramRuntime()) return;
    let cancelled = false;

    const prepareTelegramWebApp = async () => {
      const ready = await ensureTelegramWebAppScript();
      if (cancelled || !ready) return;

      const tg = window.Telegram?.WebApp;
      if (!tg || !tg.initData) return;

      // Извлекаем start_param (реферальный код или промокод)
      const startParam = tg.initDataUnsafe?.start_param;

      // Если есть реферальный код в start_param, сохраняем его
      if (startParam && startParam.startsWith('ref_')) {
        localStorage.setItem('referralCode', startParam);
      }
      // Если есть промокод в start_param (promo_<CODE>), сохраняем его
      if (startParam && startParam.startsWith('promo_')) {
        localStorage.setItem('promoCode', startParam.substring(6));
      }
    };

    prepareTelegramWebApp();
    return () => {
      cancelled = true;
    };
  }, []);

  const goToScreen = (id) => navigate(APP_ROUTES[id]);
  const isPublicShell = !protectedShell;

  return (
    <div className={`app-root ${isStudioRoute ? "app-root-studio" : ""}`.trim()} data-testid="app-root">
      <div className="bg-mesh" aria-hidden="true">
        <div className="mesh-orb orb-1" />
        <div className="mesh-orb orb-2" />
        <div className="mesh-orb orb-3" />
      </div>

      {isAuthorized ? (
        <Suspense fallback={null}>
          <Sidebar
            activeScreen={activeScreen}
            setActiveScreen={goToScreen}
            mobileOpen={menuOpen}
            onMobileClose={closeMenu}
            compact={isStudioRoute}
          />
        </Suspense>
      ) : protectedShell ? <SidebarLoadingFallback /> : null}

      <main className={`app-main ${isPublicShell ? "app-main-public" : ""} ${isStudioRoute ? "app-main-studio" : ""} ${floatingGuidesFab ? "app-main-with-fab" : ""}`.replace(/\s+/g, " ").trim()} data-testid="main-content">
        <div ref={screenWrapperRef} className={`screen-wrapper ${isPublicShell ? "screen-wrapper-public" : ""}`.trim()}>

          {/* Top bar — scrolls with content for guests, pinned for signed-in users (hosts the menu button) */}
          <div
            className={`app-header ${protectedShell ? "app-header-pinned" : ""} ${isDocsRoute ? "app-header-wide" : ""} ${isStudioRoute ? "app-header-full" : ""}`.replace(/\s+/g, " ").trim()}
          >
            <div className="app-header-left">
              {isAuthorized && !showSessionGate ? (
                <button
                  type="button"
                  className="menu-toggle"
                  aria-label={t("nav_menu_open")}
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen(true)}
                  data-testid="menu-toggle"
                >
                  <Menu size={18} />
                </button>
              ) : protectedShell ? (
                <span className="menu-toggle menu-toggle-placeholder" aria-hidden="true" />
              ) : (
                <button
                  type="button"
                  className="app-header-brand"
                  onClick={() => navigate("/")}
                  aria-label={t("header_home_aria")}
                  data-testid="header-home-btn"
                >
                  <ApilyLogo compact />
                </button>
              )}
            </div>
            <div className="app-header-right">
              {showGuidesFab && isStudioRoute ? <GuidesFab variant="inline" /> : null}
              {!protectedShell && !showSessionGate ? (
                <div className="app-header-public-links">
                  <Link
                    className="auth-trigger"
                    to="/docs/getting-started"
                    data-testid="header-docs-btn"
                  >
                    <BookOpen size={14} />
                    <span>{t("header_docs")}</span>
                  </Link>
                  <Link
                    className="auth-trigger"
                    to="/guides"
                    data-testid="header-guides-btn"
                  >
                    <Plug2 size={14} />
                    <span>{t("guides_nav")}</span>
                  </Link>
                </div>
              ) : null}
              <LanguageSwitcher />
              <NotificationsBell />
              {user && !showSessionGate ? (
                <Link
                  to="/profile"
                  className="auth-trigger"
                  data-testid="header-profile-btn"
                >
                  <User size={14} />
                  <span>{t("profile_open")}</span>
                </Link>
              ) : null}
              {!showSessionGate ? (
                <button
                  className="auth-trigger"
                  onClick={() => {
                    if (user) {
                      logout();
                    } else {
                      setAuthTab("login");
                      setAuthOpen(true);
                    }
                  }}
                  data-testid="auth-trigger-btn"
                  type="button"
                >
                  {user ? <LogOut size={14} /> : <LogIn size={14} />}
                  <span>{user ? t("logout") : t("login")}</span>
                </button>
              ) : null}
              {showSessionGate ? (
                <button
                  className="auth-trigger auth-trigger-placeholder"
                  aria-hidden="true"
                  tabIndex={-1}
                  type="button"
                />
              ) : null}
            </div>
          </div>

          {!showSessionGate && isAuthorized && !user?.email ? (
            <Suspense fallback={null}>
              <CompleteAccountModal />
            </Suspense>
          ) : null}
          {!showSessionGate && isAuthorized && !isStudioRoute ? <EmailVerificationBanner /> : null}

          {showSessionGate ? (
            <RouteLoadingFallback pathname={loadingPath} label={t("loading_hint")} />
          ) : isAuthorized ? (
            <Suspense
              fallback={<RouteLoadingFallback pathname={path} label={t("loading_hint")} />}
            >
              <Routes>
                <Route path="/home" element={<HomeScreen setActiveScreen={goToScreen} />} />
                <Route path="/api-keys" element={<ApiScreen />} />
                <Route path="/models" element={<ModelsScreen />} />
                <Route path="/models/:modelId" element={<ModelsScreen />} />
                <Route path="/docs" element={<Navigate to="/docs/getting-started" replace />} />
                <Route path="/docs/:section" element={<DocsScreen signedIn />} />
                <Route path="/referrals" element={<ReferralsScreen />} />
                <Route path="/support" element={<SupportScreen />} />
                <Route path="/functions" element={<FunctionsScreen />} />
                <Route path="/billing" element={<BillingScreen />} />
                <Route path="/profile" element={<ProfileScreen />} />
                <Route path="/studio" element={<StudioScreen />} />
                <Route path="/studio/:id" element={<StudioScreen />} />
                <Route path="/statistics" element={<Navigate to="/billing" replace />} />
                <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
                <Route path="/legal/:doc" element={<LegalScreen />} />
                <Route path="/guides" element={<PublicGuidesScreen signedIn />} />
                <Route path="/guides/:slug" element={<PublicGuideScreen signedIn />} />
                <Route path="/openai-compatible-api" element={<PublicOpenAiScreen />} />
                <Route path="/anthropic-compatible-api" element={<PublicAnthropicScreen />} />
                <Route path="/pricing" element={<PublicPricingScreen />} />
                <Route path="/compare-direct-api" element={<PublicCompareScreen />} />
                <Route path="*" element={<AuthorizedUnknownRoute />} />
              </Routes>
              {!isStudioRoute ? <SiteFooter wide={isDocsRoute} /> : null}
            </Suspense>
          ) : (
            <Suspense
              fallback={<RouteLoadingFallback pathname={path} label={t("loading_hint")} />}
            >
              <Routes>
                <Route element={<PublicBackLayout />}>
                  <Route path="/docs" element={<Navigate to="/docs/getting-started" replace />} />
                  <Route path="/docs/:section" element={<DocsScreen />} />
                  <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
                  <Route path="/legal/:doc" element={<LegalScreen />} />
                  <Route path="/guides" element={<PublicGuidesScreen />} />
                  <Route path="/guides/:slug" element={<PublicGuideScreen />} />
                  <Route path="/models" element={<ModelsScreen />} />
                  <Route path="/models/:modelId" element={<ModelsScreen />} />
                  <Route path="/openai-compatible-api" element={<PublicOpenAiScreen />} />
                  <Route path="/anthropic-compatible-api" element={<PublicAnthropicScreen />} />
                  <Route path="/pricing" element={<PublicPricingScreen />} />
                  <Route path="/compare-direct-api" element={<PublicCompareScreen />} />
                </Route>
                <Route
                  path="/"
                  element={
                    <LandingScreen
                      onAuthOpen={(tab = "register") => {
                        setAuthTab(tab);
                        setAuthOpen(true);
                      }}
                    />
                  }
                />
                <Route path="*" element={<GuestUnknownRoute />} />
              </Routes>
              {location.pathname !== "/" ? <SiteFooter wide={isDocsRoute} /> : null}
            </Suspense>
          )}
        </div>
      </main>

      {floatingGuidesFab ? <GuidesFab /> : null}

      <Suspense fallback={null}>
        <Toaster position="top-center" richColors />
      </Suspense>
      <Suspense fallback={null}>
        <AuthModal
          open={authOpen}
          onClose={() => {
            setAuthOpen(false);
            clearTwoFactor();
          }}
          initialTab={authTab}
        />
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <UserProvider>
          <AppInner />
        </UserProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
